import {
	Button,
	DropdownMenuItem,
	Input,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { Plus } from "lucide-react";
import { useId } from "react";
import type { Block } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { ArticleActions } from "~/modules/analytics/components/article-actions";
import { ConfirmRemoval } from "~/modules/analytics/components/confirm-removal";
import { WritingField } from "~/modules/analytics/components/writing-field";
import { sourceIdentity } from "~/modules/analytics/lib/sources";

/** The cap the whole answer block exists for. Words, not characters. */
export const ANSWER_MAX_WORDS = 80;

/**
 * The same paragraph in Chinese, where `words` counts each Han character. A
 * quotable Chinese paragraph runs about twice its English equivalent, so one
 * number for both languages refuses every Chinese page. Mirrors the server's
 * `ANSWER_MAX_HAN`; the editor has to refuse exactly what the server refuses.
 */
export const ANSWER_MAX_HAN = 160;

/** Which cap this text is held to, decided by what it is written in. */
export function answerCap(text: string): number {
	const han = (text.match(/[㐀-鿿]/g) ?? []).length;
	return han * 2 > words(text) ? ANSWER_MAX_HAN : ANSWER_MAX_WORDS;
}

/**
 * One editor per block type.
 *
 * The set is closed on the server — the renderer, the JSON-LD builder and the
 * fact-checker all have to agree about what a page contains — so this file is
 * the same closed set, in the same vocabulary. An editor that let somebody type
 * HTML would be an editor that produces pages the checker cannot read.
 */
export function BlockEditor({
	block,
	onChange,
	disabled = false,
}: {
	block: Block;
	onChange: (next: Block) => void;
	disabled?: boolean;
}) {
	const t = useT();
	const id = useId();

	switch (block.type) {
		case "answer":
			return (
				<div className="field">
					<span className="hint">{t("block.answerHint")}</span>
					<WritingField
						value={block.text}
						label={t("block.answer")}
						disabled={disabled}
						plain
						onChange={(text) => onChange({ ...block, text })}
					/>
					<WordCount text={block.text} />
				</div>
			);

		case "section":
			return (
				<>
					<div className="field">
						<Label htmlFor={`${id}-heading`}>{t("block.sectionHeading")}</Label>
						<Input
							id={`${id}-heading`}
							value={block.heading}
							onChange={(e) => onChange({ ...block, heading: e.target.value })}
						/>
					</div>
					<div className="field">
						<Label htmlFor={`${id}-body`}>{t("block.sectionBody")}</Label>
						<WritingField
							value={block.md}
							label={t("block.sectionBody")}
							disabled={disabled}
							onChange={(md) => onChange({ ...block, md })}
						/>
						<span className="hint">{t("block.sectionHint")}</span>
					</div>
				</>
			);

		case "faq":
			return (
				<div className="stack">
					{block.items.map((item, index) => (
						// The index is the identity: these rows have no id, and a key
						// made from the text would remount the field being typed in.
						// biome-ignore lint/suspicious/noArrayIndexKey: no stable id on a FAQ row
						<div className="sub-row article-faq-row" key={index}>
							<WritingField
								plain
								disabled={disabled}
								label={t("block.faqQ")}
								value={item.q}
								onChange={(q) => onChange({ ...block, items: replace(block.items, index, { q }) })}
							/>
							<WritingField
								plain
								disabled={disabled}
								label={t("block.faqA")}
								value={item.a}
								onChange={(a) => onChange({ ...block, items: replace(block.items, index, { a }) })}
							/>
							<div className="article-item-tools">
								<ArticleActions
									label={`${t("block.faq")} ${index + 1}`}
									disabled={disabled}
									remove={() => onChange({ ...block, items: block.items.filter((_, i) => i !== index) })}
								>
									<DropdownMenuItem
										onSelect={() =>
											onChange({
												...block,
												items: [...block.items.slice(0, index + 1), { q: "", a: "" }, ...block.items.slice(index + 1)],
											})
										}
									>
										<Plus />
										{t("block.addFaq")}
									</DropdownMenuItem>
								</ArticleActions>
							</div>
						</div>
					))}
					{!block.items.length && (
						<AddRow
							label={t("block.addFaq")}
							onClick={() => onChange({ ...block, items: [...block.items, { q: "", a: "" }] })}
						/>
					)}
				</div>
			);

		case "sources":
			return (
				<div className="stack article-sources">
					{block.items.map((item, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: source rows do not have persisted identifiers
						<div className="article-source-row" key={index}>
							<Input
								disabled={disabled}
								placeholder={t("block.sourceLabel")}
								aria-label={t("block.sourceLabel")}
								value={item.label}
								onChange={(e) => onChange({ ...block, items: replace(block.items, index, { label: e.target.value }) })}
							/>
							<Input
								disabled={disabled}
								type="url"
								placeholder="https://"
								aria-label={t("block.sourceUrl")}
								value={item.url}
								onChange={(e) => onChange({ ...block, items: replace(block.items, index, { url: e.target.value }) })}
							/>
							<ArticleActions
								label={`${t("block.sourceLabel")} ${index + 1}`}
								disabled={disabled}
								remove={() => onChange({ ...block, items: block.items.filter((_, i) => i !== index) })}
							>
								<DropdownMenuItem
									onSelect={() =>
										onChange({
											...block,
											items: [
												...block.items.slice(0, index + 1),
												{ label: "", url: "" },
												...block.items.slice(index + 1),
											],
										})
									}
								>
									<Plus />
									{t("block.addSource")}
								</DropdownMenuItem>
							</ArticleActions>
						</div>
					))}
					{!block.items.length && (
						<AddRow
							label={t("block.addSource")}
							onClick={() => onChange({ ...block, items: [{ label: "", url: "" }] })}
						/>
					)}
				</div>
			);

		case "stat":
			return (
				<div className="sub-row inline">
					<Input
						placeholder={t("block.statLabel")}
						aria-label={t("block.statLabel")}
						value={block.label}
						onChange={(e) => onChange({ ...block, label: e.target.value })}
					/>
					<Input
						placeholder={t("block.statValue")}
						aria-label={t("block.statValue")}
						value={block.value}
						onChange={(e) => onChange({ ...block, value: e.target.value })}
					/>
					<Input
						placeholder={t("block.statUnit")}
						aria-label={t("block.statUnit")}
						value={block.unit ?? ""}
						onChange={(e) => onChange({ ...block, unit: e.target.value || null })}
					/>
					<Input
						type="url"
						placeholder="https://"
						aria-label={t("block.sourceUrl")}
						value={block.source_url ?? ""}
						onChange={(e) => onChange({ ...block, source_url: e.target.value || null })}
					/>
				</div>
			);

		case "table":
			return <TableEditor block={block} onChange={onChange} />;

		case "chart":
			// Deliberately not editable. The series is a measurement; a chart
			// somebody retyped is a chart that no longer says what was measured.
			return (
				<div className="stack-tight">
					<strong>{block.title}</strong>
					<span className="hint">{t("block.chartLocked")}</span>
				</div>
			);
	}
}

function TableEditor({
	block,
	onChange,
}: {
	block: Extract<Block, { type: "table" }>;
	onChange: (next: Block) => void;
}) {
	const t = useT();
	const id = useId();

	function setCell(row: number, column: number, value: string) {
		const rows = block.rows.map((cells, i) => (i === row ? cells.map((c, j) => (j === column ? value : c)) : cells));
		onChange({ ...block, rows });
	}

	return (
		<div className="stack">
			<div className="field">
				<Label htmlFor={`${id}-caption`}>{t("block.tableCaption")}</Label>
				<Input
					id={`${id}-caption`}
					value={block.caption ?? ""}
					onChange={(e) => onChange({ ...block, caption: e.target.value || null })}
				/>
			</div>

			<div className="table-scroll article-table">
				<Table variant="resource">
					<TableHeader>
						<TableRow>
							{block.columns.map((column, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: column position is the identity
								<TableHead key={index}>
									<Input
										value={column}
										aria-label={`${t("block.table")} ${index + 1}`}
										onChange={(e) =>
											onChange({
												...block,
												columns: block.columns.map((c, i) => (i === index ? e.target.value : c)),
											})
										}
									/>
								</TableHead>
							))}
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{block.rows.map((cells, row) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: row position is the identity
							<TableRow key={row}>
								{block.columns.map((_, column) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: cell position is the identity
									<TableCell key={column}>
										<Input
											aria-label={`${block.columns[column]} · ${row + 1}`}
											value={cells[column] ?? ""}
											onChange={(e) => setCell(row, column, e.target.value)}
										/>
									</TableCell>
								))}
								<TableCell className="num">
									<RemoveRow
										label={t("block.removeRow")}
										onClick={() => onChange({ ...block, rows: block.rows.filter((_, i) => i !== row) })}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			<div className="row-actions">
				<AddRow
					label={t("block.addRow")}
					onClick={() => onChange({ ...block, rows: [...block.rows, block.columns.map(() => "")] })}
				/>
				<AddRow
					label={t("block.addColumn")}
					onClick={() =>
						onChange({
							...block,
							columns: [...block.columns, ""],
							// Widen every existing row at the same time: a row narrower
							// than the header is exactly what the server rejects.
							rows: block.rows.map((cells) => [...cells, ""]),
						})
					}
				/>
			</div>
		</div>
	);
}

function WordCount({ text }: { text: string }) {
	const t = useT();
	const n = words(text);
	const max = answerCap(text);
	const over = n > max;
	return (
		<span className={over ? "hint warn" : "hint"}>
			{t("editor.words", { n, max })}
			{over ? ` · ${t("editor.answerLong")}` : ""}
		</span>
	);
}

/**
 * The word count the 80-word cap is measured against.
 *
 * Chinese has no spaces, so splitting on whitespace would report a 300-word
 * Chinese answer as one word and let it through a cap that exists to keep the
 * paragraph quotable. Han characters count one each, which is close enough to
 * how much of an answer the paragraph fills.
 */
export function words(text: string): number {
	const han = (text.match(/[㐀-鿿]/g) ?? []).length;
	const latin = text.split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word.replace(/[㐀-鿿]/g, ""))).length;
	return han + latin;
}

function replace<T>(items: T[], index: number, patch: Partial<T>): T[] {
	return items.map((item, i) => (i === index ? { ...item, ...patch } : item));
}

function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<Button variant="ghost" size="icon" type="button" onClick={onClick} aria-label={label} title={label}>
			<Plus aria-hidden />
		</Button>
	);
}

function RemoveRow({ label, onClick }: { label: string; onClick: () => void }) {
	return <ConfirmRemoval label={label} onConfirm={onClick} />;
}

/** A new block of each type, in the shape the server will accept. */
export function emptyBlock(type: Block["type"]): Block {
	switch (type) {
		case "answer":
			return { type: "answer", text: "" };
		case "section":
			return { type: "section", heading: "", md: "" };
		case "faq":
			return { type: "faq", items: [{ q: "", a: "" }] };
		case "table":
			return { type: "table", caption: null, columns: ["", ""], rows: [["", ""]] };
		case "sources":
			return { type: "sources", items: [{ label: "", url: "" }] };
		case "stat":
			return { type: "stat", label: "", value: "", unit: null, source_url: null };
		case "chart":
			return { type: "chart", title: "", kind: "line", series: [] };
	}
}

/**
 * The server's rules, checked here first.
 *
 * Not instead of the server — the server is the authority and validates every
 * edit — but before it. A form that lets somebody rewrite a page and then
 * refuses the save teaches them that the rule is an obstacle; a form that says
 * "the answer has to come first" while they are still editing teaches them what
 * the rule is for.
 */
export function problems(blocks: Block[], generated: boolean, t: (key: string) => string): string[] {
	const found: string[] = [];

	if (generated) {
		const answers = blocks.filter((b) => b.type === "answer");
		if (answers.length !== 1 || blocks[0]?.type !== "answer") found.push(t("editor.rule.oneAnswer"));
	}

	if (blocks.some((b) => b.type === "table" && b.rows.some((row) => row.length !== b.columns.length))) {
		found.push(t("editor.rule.tableWidth"));
	}

	if (blocks.some((b) => b.type === "answer" && words(b.text) > answerCap(b.text))) found.push(t("editor.answerLong"));
	if (
		blocks.some(
			(b) =>
				(b.type === "sources" && b.items.some((item) => !sourceIdentity(item.url))) ||
				(b.type === "stat" && b.source_url != null && !sourceIdentity(b.source_url)),
		)
	)
		found.push(t("editor.sourceInvalid"));
	if (!blocks.length || blocks.some(isEmpty)) found.push(t("editor.rule.empty"));

	return found;
}

function isEmpty(block: Block): boolean {
	switch (block.type) {
		case "answer":
			return !block.text.trim();
		case "section":
			return !block.heading.trim() || !block.md.trim();
		case "faq":
			return block.items.length === 0 || block.items.some((i) => !i.q.trim() || !i.a.trim());
		case "sources":
			return block.items.length === 0 || block.items.some((i) => !i.url.trim());
		case "stat":
			return !block.label.trim() || !block.value.trim();
		case "table":
			return block.columns.length === 0 || block.rows.length === 0;
		case "chart":
			return block.series.length === 0;
	}
}
